import { getTimeAgo, getTimeAgoLong } from '@news-mono/web-common'
import { format as formatToTimeZone, utcToZonedTime } from 'date-fns-tz'
import format from 'date-fns/format'
import enAU from 'date-fns/locale/en-AU'
import React from 'react'
import {
    ClockIcon,
    ClockIconAlt,
    StyledTimestamp,
} from '../../typography/Timestamp/Timestamp.styled'
import { useProduct } from '../../__product/useProduct'
import { ThemedRender } from '../../__styling/themed-render'

const componentTheme = {
    sevennews: {
        timestamp: {
            dayMonthYear: 'dd/MM/yyyy',
            dateOnly: 'EEEE, d MMMM yyyy',
            dateTime: 'EEEE, d MMMM yyyy h:mm a',
            dateTimeOnly: 'd MMMM yyyy h:mm a',
            dateTimeShortDay: 'EEE, d MMMM yyyy h:mm a',
            dateTimeShortDayNoTime: 'EEE, d MMMM yyyy',
            dateTimeWithZone: 'EEEE, d MMMM yyyy h:mm a zzz',
            dateTimeOnlyWithZone: 'd MMMM yyyy h:mm a zzz',
        },
        timezone: 'Australia/Sydney',
    },
    thewest: {
        timestamp: {
            dayMonthYear: 'dd/MM/yyyy',
            dateOnly: 'EEEE, d MMMM yyyy',
            dateTime: 'EEEE, d MMMM yyyy h:mma',
            dateTimeOnly: 'd MMMM yyyy h:mma',
            dateTimeShortDay: 'EEE, d MMMM yyyy h:mma',
            dateTimeShortDayNoTime: 'EEE, d MMMM yyyy',
            dateTimeWithZone: 'EEEE, d MMMM yyyy h:mm a zzz',
            dateTimeOnlyWithZone: 'd MMMM yyyy h:mm a zzz',
        },
        timezone: 'Australia/Perth',
    },
    perthnow: {
        timestamp: {
            dayMonthYear: 'dd/MM/yyyy',
            dateOnly: 'MMMM d, yyyy',
            dateTime: 'MMMM d, yyyy h:mma',
            dateTimeOnly: 'd MMMM yyyy h:mma',
            dateTimeShortDay: 'EEE, d MMMM yyyy h:mma',
            dateTimeShortDayNoTime: 'EEE, d MMMM yyyy',
            dateTimeWithZone: 'EEEE, d MMMM yyyy h:mm a zzz',
            dateTimeOnlyWithZone: 'd MMMM yyyy h:mm a zzz',
        },
        timezone: 'Australia/Perth',
    },
    // Todo: update this for theNightly
    thenightly: {
        timestamp: {
            dayMonthYear: 'dd/MM/yyyy',
            dateOnly: 'EEEE, d MMMM yyyy',
            dateTime: 'EEEE, d MMMM yyyy h:mma',
            dateTimeOnly: 'd MMMM yyyy h:mma',
            dateTimeShortDay: 'EEE, d MMMM yyyy h:mma',
            dateTimeShortDayNoTime: 'EEE, d MMMM yyyy',
            dateTimeWithZone: 'EEEE, d MMMM yyyy h:mm a zzz',
            dateTimeOnlyWithZone: 'd MMMM yyyy h:mm a zzz',
        },
        timezone: 'Australia/Perth',
    },
}

export type TimestampFormat =
    | 'dateOnly'
    | 'relative'
    | 'relativeLongFormat'
    | 'dateTimeOnly'
    | 'dayMonthYear'
    | 'dateTimeShortDay'
    | 'dateTimeShortDayNoTime'
    | 'default'
    | 'customSevenNewsDateTime'
    | 'customSevenNewsDateTimeOnly'

export type TimestampLabel =
    | 'Published:'
    | 'Updated:'
    | 'Last updated'
    | 'Updated'
    | ''

export interface TimestampProps {
    timestamp: string | number
    iconVisible?: boolean
    className?: string
    timestampFormat?: TimestampFormat
    timestampLabel?: TimestampLabel
}

export const Timestamp: React.FC<TimestampProps> = ({
    timestamp,
    iconVisible,
    className,
    timestampFormat = 'default',
    timestampLabel = undefined,
}) => {
    const product = useProduct()

    if (!timestamp) {
        return null
    }
    const themeValues = componentTheme[product].timestamp
    const themeTimezoneValue = componentTheme[product].timezone

    const getHumanReadableTimestamp = (timestampFormat: TimestampFormat) => {
        switch (timestampFormat) {
            case 'relative':
                return getTimeAgo(new Date(timestamp))
            case 'relativeLongFormat':
                return getTimeAgoLong(new Date(timestamp))
            case 'dateOnly':
                return format(new Date(timestamp), themeValues.dateOnly)
            case 'dateTimeShortDayNoTime':
                return format(
                    new Date(timestamp),
                    themeValues.dateTimeShortDayNoTime,
                )
            case 'dayMonthYear':
                return format(new Date(timestamp), themeValues.dayMonthYear)
            case 'dateTimeOnly':
                return format(new Date(timestamp), themeValues.dateTimeOnly)
            case 'dateTimeShortDay':
                return format(new Date(timestamp), themeValues.dateTimeShortDay)
            case 'customSevenNewsDateTime':
                return formatToTimeZone(
                    utcToZonedTime(new Date(timestamp), themeTimezoneValue),
                    themeValues.dateTimeWithZone,
                    {
                        timeZone: themeTimezoneValue,
                        locale: enAU,
                    },
                )
            case 'customSevenNewsDateTimeOnly':
                return formatToTimeZone(
                    utcToZonedTime(new Date(timestamp), themeTimezoneValue),
                    themeValues.dateTimeOnlyWithZone,
                    {
                        timeZone: themeTimezoneValue,
                        locale: enAU,
                    },
                )
            case 'default':
                return format(new Date(timestamp), themeValues.dateTime)
            default:
                return
        }
    }

    const html5datetime = format(new Date(timestamp), "yyyy-MM-dd'T'HH:mmXXXX")

    const Icon = (
        <ThemedRender
            thewest={() => <ClockIcon />}
            sevennews={() => <ClockIconAlt />}
        />
    )

    return (
        <StyledTimestamp
            dateTime={html5datetime}
            className={className}
            suppressHydrationWarning={true}
        >
            {iconVisible && Icon}
            {timestampLabel && timestampLabel + ' '}

            {getHumanReadableTimestamp(timestampFormat)}
        </StyledTimestamp>
    )
}
Timestamp.displayName = 'Timestamp'
